import { useLanguage } from "@/components/language-provider";
import { QrCode } from "lucide-react";

/** Where the verification QR goes on signed PDFs (Settings). The code and link always stay as text. */
export type VerificationQrMode = "every_page" | "last_page" | "none";

const TEXTS = {
  pt: {
    title: "QR code de verificação no PDF assinado",
    hint: "O código e o link de verificação ficam sempre no rodapé, em texto. Aqui você escolhe onde vai o QR code.",
    every_page: "Em todas as páginas",
    every_pageHint: "Padrão. Quem tiver qualquer página confere na hora.",
    last_page: "Só na última página",
    last_pageHint: "As outras páginas ficam só com o texto no rodapé.",
    none: "Sem QR code",
    noneHint: "Só o código e o link, em texto, em todas as páginas.",
    applies: "Vale para os documentos assinados a partir de agora.",
  },
  en: {
    title: "Verification QR code on the signed PDF",
    hint: "The verification code and link always stay in the footer, as text. Here you choose where the QR code goes.",
    every_page: "On every page",
    every_pageHint: "Default. Anyone holding any page can check it at once.",
    last_page: "Last page only",
    last_pageHint: "The other pages keep only the footer text.",
    none: "No QR code",
    noneHint: "Only the code and link, as text, on every page.",
    applies: "Applies to documents signed from now on.",
  },
};

/** Three tiny pages showing where the QR lands. */
function Pages({ mode }: { mode: VerificationQrMode }) {
  return (
    <div className="flex items-end gap-1" aria-hidden>
      {[0, 1, 2].map((i) => {
        const qr = mode === "every_page" || (mode === "last_page" && i === 2);
        return (
          <div key={i} className="relative h-10 w-8 rounded-sm border bg-background shadow-sm">
            <div className="mx-1 mt-1.5 space-y-0.5">
              <div className="h-0.5 rounded bg-muted-foreground/30" />
              <div className="h-0.5 w-4/5 rounded bg-muted-foreground/30" />
              <div className="h-0.5 w-3/5 rounded bg-muted-foreground/30" />
            </div>
            <div className="absolute bottom-1 left-1 h-0.5 w-3 rounded bg-muted-foreground/40" />
            {qr && <QrCode className="absolute bottom-0.5 right-0.5 h-3 w-3 text-foreground" />}
          </div>
        );
      })}
    </div>
  );
}

export function VerificationQrSetting({ value, onChange, disabled }: { value: VerificationQrMode; onChange: (mode: VerificationQrMode) => void; disabled?: boolean }) {
  const { language } = useLanguage();
  const tx = TEXTS[language === "en" ? "en" : "pt"];
  const modes: VerificationQrMode[] = ["every_page", "last_page", "none"];
  return (
    <div className="space-y-3" data-testid="verification-qr-setting">
      <div className="space-y-1">
        <p className="flex items-center gap-2 text-sm font-medium"><QrCode className="h-4 w-4 text-muted-foreground" />{tx.title}</p>
        <p className="text-sm text-muted-foreground">{tx.hint}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label={tx.title}>
        {modes.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={value === m}
            disabled={disabled}
            onClick={() => value !== m && onChange(m)}
            className={`flex flex-col items-start gap-2 rounded-lg border-2 p-3 text-left transition-smooth disabled:opacity-60 ${value === m ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
            data-testid={`qr-mode-${m}`}
          >
            <Pages mode={m} />
            <span className="text-sm font-medium">{tx[m]}</span>
            <span className="text-xs text-muted-foreground">{tx[`${m}Hint` as const]}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{tx.applies}</p>
    </div>
  );
}
